import React, { useState } from 'react';
import ShareButton from './ShareButton';
import { downloadVCard } from '../utils/contactUtils';
import { EnvelopeIcon, PhoneIcon } from '@heroicons/react/24/outline';
import { checkCardAccess } from '../utils/cardAccess';
import { Edit, Share2 } from 'lucide-react';

export default function Flashcard({ frontContent, backContent, onEdit, originalCard, onShare, currentUser }) {
  const [isFlipped, setIsFlipped] = useState(false);
  
  const { canEdit } = checkCardAccess(originalCard, currentUser?.uid);

  const handleFrontClick = () => {
    setIsFlipped(true);
  };

  const handleBackClick = (e) => {
    e.stopPropagation();
    setIsFlipped(false);
  };

  const handleEditClick = (e) => {
    e.stopPropagation();
    console.log('Edit button clicked');
    console.log('Can edit:', canEdit);
    console.log('Original card:', originalCard);
    if (canEdit) {
      console.log('Calling onEdit with card:', originalCard);
      onEdit(originalCard);
    } else {
      console.log('User does not have edit permissions');
    }
  };

  const handleLinkClick = (e) => {
    e.stopPropagation();
  };

  return (
    <div className="relative w-full h-[425px] perspective-1000">
      <div className={`relative w-full h-full duration-500 transform-style-preserve-3d ${
        isFlipped ? 'rotate-y-180' : ''
      }`}>
        {/* Front of card */}
        <div className="absolute w-full h-full bg-white rounded-lg shadow-lg backface-hidden">
          <div 
            onClick={handleFrontClick}
            className="flex flex-col items-center justify-center w-full h-full p-6 cursor-pointer"
            role="button"
            aria-label="Click to reveal details"
          >
            {frontContent}
          </div>
        </div>
        
        {/* Back of card */}
        <div className="absolute w-full h-full bg-white rounded-lg shadow-lg backface-hidden rotate-y-180 overflow-y-auto">
          {/* Navigation Bar */}
          <div className="sticky top-0 left-0 right-0 bg-white border-b z-20">
            <button
              onClick={handleBackClick}
              className="w-full p-3 flex items-center text-gray-600 hover:text-[#04b53a] hover:bg-gray-50 transition-colors group"
              aria-label="Back to front"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
              </svg>
              <span className="font-medium">Back to photo</span>
            </button>
          </div>

          {/* Card content with circular photo */}
          <div className="p-6 relative" onClick={(e) => e.stopPropagation()}>
            {/* Small circular photo */}
            <div className="absolute top-4 left-1/2 transform -translate-x-1/2">
              <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-white shadow-lg">
                {originalCard.imageUrl ? (
                  <img
                    src={originalCard.imageUrl}
                    alt={`${originalCard.firstName} ${originalCard.lastName}`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gray-100 flex items-center justify-center text-gray-400">
                    {originalCard.firstName?.charAt(0) || '?'}
                  </div>
                )}
              </div>
            </div>
            {/* Reduced margin-top to bring content closer to photo */}
            <div className="mt-14">
              {backContent}
            </div>
          </div>

          {/* Footer with Edit and Share buttons */}
          <div className="sticky bottom-0 left-0 right-0 bg-white border-t z-20">
            <div className="grid grid-cols-3">
              {canEdit && (
                <button
                  onClick={handleEditClick}
                  className="p-3 flex items-center justify-center text-gray-600 hover:text-[#04b53a] hover:bg-gray-50 transition-colors border-r"
                  aria-label="Edit card"
                >
                  <Edit className="w-5 h-5 mr-2" />
                  <span className="font-medium">Edit card</span>
                </button>
              )}
              <ShareButton 
                card={originalCard}
                onShare={onShare}
              />
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  downloadVCard(originalCard);
                }}
                className="p-3 flex items-center justify-center text-gray-600 hover:text-[#04b53a] hover:bg-gray-50 transition-colors"
                title="Add to Contacts"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
                </svg>
                <span className="font-medium">Add contact</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Only keep LinkedInIcon since it's not from heroicons
const LinkedInIcon = () => (
  <svg 
    className="w-4 h-4 inline-block mr-1" 
    fill="currentColor" 
    viewBox="0 0 24 24"
  >
    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
  </svg>
);

export const createCardContent = (card, onNavigate) => {
  console.log('Creating card content with:', card); // Debug log
  const groups = Array.isArray(card.groups) ? card.groups : [card.group];
  
  return (
    <div className="space-y-4">
      {/* Header with full name */}
      <h2 className="text-2xl font-semibold text-[#04b53a] text-center">
        {card.firstName} {card.lastName}
      </h2>

      {/* Groups */}
      <div className="flex flex-wrap gap-2">
        {(card.groups || [card.group]).map((group, index) => (
          <span
            key={index}
            className="inline-block bg-gray-100 rounded-full px-3 py-1 text-sm font-semibold text-gray-700"
          >
            {group}
          </span>
        ))}
      </div>

      {/* Professional Section */}
      {(card.company || card.role || card.linkedIn) && (
        <div className="space-y-2">
          <h3 className="font-medium text-gray-900">Professional</h3>
          {card.company && <p className="text-gray-700">{card.company}</p>}
          {card.role && <p className="text-gray-700">{card.role}</p>}
          {card.linkedIn && (
            <a 
              href={card.linkedIn}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              className="text-blue-600 hover:text-blue-800 flex items-center"
            >
              <LinkedInIcon />
              View Profile
            </a>
          )}
        </div>
      )}

      {/* Family Section */}
      {(card.relationships?.spouse || 
        card.relationships?.parents?.length > 0 || 
        card.relationships?.children?.length > 0) && (
        <div className="space-y-2">
          <h3 className="font-medium text-gray-900">Family</h3>
          
          {card.relationships.spouse && (
            <p className="text-gray-700">
              Spouse:{' '}
              <button
                onClick={() => onNavigate(card.relationships.spouse.cardId)}
                className="text-blue-600 hover:underline"
              >
                {card.relationships.spouse.name}
              </button>
            </p>
          )}

          {card.relationships.parents?.length > 0 && (
            <p className="text-gray-700">
              Parents:{' '}
              {card.relationships.parents.map((parent, index) => (
                <React.Fragment key={parent.cardId}>
                  <button
                    onClick={() => onNavigate(parent.cardId)}
                    className="text-blue-600 hover:underline"
                  >
                    {parent.name}
                  </button>
                  {index < card.relationships.parents.length - 1 ? ', ' : ''}
                </React.Fragment>
              ))}
            </p>
          )}

          {card.relationships.children?.length > 0 && (
            <p className="text-gray-700">
              Children:{' '}
              {card.relationships.children.map((child, index) => (
                <React.Fragment key={child.cardId}>
                  <button
                    onClick={() => onNavigate(child.cardId)}
                    className="text-blue-600 hover:underline"
                  >
                    {child.name}
                  </button>
                  {index < card.relationships.children.length - 1 ? ', ' : ''}
                </React.Fragment>
              ))}
            </p>
          )}
        </div>
      )}

      {/* Notes Section */}
      {card.info && (
        <div className="space-y-2">
          <h3 className="font-medium text-gray-900">Notes</h3>
          <p className="text-gray-700 text-left">{card.info}</p>
        </div>
      )}

      {/* Email Section */}
      {card.email && (
        <div className="flex items-center text-gray-600 text-sm mb-1">
          <EnvelopeIcon className="w-4 h-4 mr-1" />
          <a href={`mailto:${card.email}`} className="hover:text-blue-600 hover:underline">
            {card.email}
          </a>
        </div>
      )}

      {/* Phone Section */}
      {card.phone && (
        <div className="flex items-center text-gray-600 text-sm mb-1">
          <PhoneIcon className="w-4 h-4 mr-1" />
          <a href={`tel:${card.phone}`} className="hover:text-blue-600 hover:underline">
            {card.phone}
          </a>
        </div>
      )}
    </div>
  );
}